import type { SidebarNavItem, TeamRole } from "../../config/sidebarConfig";
import { librarySections } from "../../utils/libraryNav";
import type { AuthUser } from "../../types/authUser";
import {
  getFeatureFlagsFromUser,
  isFeatureFlagEnabled,
} from "../../utils/featureFlags";

/**
 * - `shown`: listed and clickable
 * - `disabled`: listed but greyed out until the school is approved
 * - `hidden`: not listed (team role, feature flag or organization type)
 */
export type SidebarItemAccess = "shown" | "disabled" | "hidden";

/**
 * The sidebar's visibility rules, shared with anything that must not offer
 * more than the sidebar does (e.g. Ask Noticeboard). Hidden wins over disabled.
 */
export function getSidebarItemAccess(
  item: SidebarNavItem,
  user: AuthUser | null | undefined
): SidebarItemAccess {
  const isTeamMember = user?.userType === "team";
  // Team members are approved; org admins need their school approved
  const isApproved = isTeamMember || Boolean(user?.isSchoolApproved);
  // Org admins have access to all items regardless of teamRoles
  const hasRoleAccess = isTeamMember
    ? item.teamRoles.includes(user?.accessLevel as TeamRole)
    : true;

  const flags = getFeatureFlagsFromUser(user);
  const normalizedSchoolType =
    typeof user?.schoolType === "string"
      ? user.schoolType.toLowerCase().trim()
      : "";
  const isSchoolOrg = normalizedSchoolType === "school";
  const isChurchOrg = normalizedSchoolType === "church";
  const isOrganizationOrg = normalizedSchoolType === "organization";
  const sections = librarySections(user);
  const libraryHubVisible = sections.worksheets || sections.resources;

  const isLibraryHidden = Boolean(item.libraryHub && !libraryHubVisible);
  const isSchoolOnlyHidden = Boolean(
    item.schoolOnly && !isSchoolOrg && !isOrganizationOrg
  );
  const isChurchOnlyHidden = Boolean(
    item.churchOnly && !isChurchOrg && !isOrganizationOrg
  );
  const { featureFlagKey } = item;
  const isFeatureHidden =
    !item.libraryHub &&
    featureFlagKey !== undefined &&
    !isFeatureFlagEnabled(flags, featureFlagKey);

  if (
    !hasRoleAccess ||
    isFeatureHidden ||
    isSchoolOnlyHidden ||
    isChurchOnlyHidden ||
    isLibraryHidden
  ) {
    return "hidden";
  }
  if (!isApproved && item.requiresApproval) {
    return "disabled";
  }
  return "shown";
}

/** Can this person open this sidebar item right now? */
export function canOpenSidebarItem(
  item: SidebarNavItem,
  user: AuthUser | null | undefined
): boolean {
  return getSidebarItemAccess(item, user) === "shown";
}
